"use client"

import * as React from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm } from '@open-mercato/ui/backend/CrudForm'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { createCrudFormError, normalizeCrudServerError } from '@open-mercato/ui/backend/utils/serverErrors'
import {
  type CargoFormValues,
  buildCargoFields,
  buildCargoGroups,
  buildCargoPayload,
  buildCargoFormSchema,
  buildInitialCargoValues,
} from '../../../lib/cargoForm'

export default function CreateCargoPage() {
  const t = useT()
  const router = useRouter()
  const searchParams = useSearchParams()
  const salesShipmentId = searchParams.get('salesShipmentId')

  const fields = React.useMemo(() => buildCargoFields(t), [t])
  const groups = React.useMemo(() => buildCargoGroups(t), [t])
  const schema = React.useMemo(() => buildCargoFormSchema(t), [t])
  const initialValues = React.useMemo(
    () => buildInitialCargoValues({ salesShipmentId }),
    [salesShipmentId],
  )

  return (
    <Page>
      <PageBody>
        <CrudForm<CargoFormValues>
          title={t('cargo.pages.create.title', 'Register cargo')}
          backHref="/backend/cargo"
          cancelHref="/backend/cargo"
          submitLabel={t('cargo.pages.create.submit', 'Register cargo')}
          fields={fields}
          groups={groups}
          initialValues={initialValues}
          schema={schema}
          onSubmit={async (values) => {
            const payload = buildCargoPayload(values)
            let created: Awaited<ReturnType<typeof createCrud<{ id?: string }>>>
            try {
              created = await createCrud<{ id?: string }>('freight/cargo', payload, {
                errorMessage: t('cargo.pages.create.saveError', 'Failed to register cargo.'),
              })
            } catch (err) {
              const normalized = normalizeCrudServerError(err)
              throw createCrudFormError(
                normalized.message ?? t('cargo.pages.create.saveError', 'Failed to register cargo.'),
                normalized.fieldErrors,
              )
            }
            flash(t('cargo.pages.create.success', 'Cargo registered successfully.'), 'success')
            const id = created.result?.id
            router.push(id ? `/backend/cargo/${id}` : '/backend/cargo')
          }}
        />
      </PageBody>
    </Page>
  )
}
